import _find from 'lodash/find';
import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import Analytics from '@web-solutions/module-analytics';
import { SOLIDGATE_ERROR_CODES } from '@web-solutions/core/constants/errors';
import type { ImageSource } from '@web-solutions/core/interfaces/images';
import { getProductsTexts } from '@web-solutions/core/utils/products';
import { tKey as specialOfferTKey } from 'core/summary/special-offer/constants';
import {
  ButtonPlacementType,
  ButtonBehavior,
  ButtonConfig,
  InterButtonConfig,
} from 'core/payment/constants';
import { scrollToSection } from 'core/utils/scroll-to-plans';
import { init, initNoFundsOfferEndDate, initSpecialOfferEndDate } from 'core/store/billing/actions';
import {
  ProductDetails,
  selectProducts,
  selectLoading,
  selectSpecialOfferEndDate,
  selectNoFundsOfferEndDate,
  selectSpecialOfferProducts,
} from 'core/store/billing/selectors';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { InertialApplePay } from 'core/constants/billing';
import { useShowInertialApplePay } from 'core/hooks/use-show-payment-method';
import { useSummaryViewedRate } from 'core/hooks/use-summary-viewed-rate';
import type { PaymentModalTitle } from 'core/interfaces/payment-modal';
import type { Subscription } from 'core/interfaces/billing';
import { Preloader } from 'core/ui-elements';
import { PaymentProcessor, PaymentProcessorRef } from 'core/payment';
import classes from './style.module.scss';
import { CoreSummarySection } from 'core/summary/types';
import { Plans } from 'core/summary/plans';
import {
  SpecialOfferInfo,
  SpecialOfferNoFunds,
  SpecialOfferWithPlans,
} from 'core/summary/special-offer';
import { FixedButton } from 'core/summary/fixed-button';
import { TrialPrice } from 'core/summary/trial-price';
import { DownSaleOfferModal } from 'core/summary/down-sale-modal-offer';
import { useAppSelector } from '@web-solutions/store/types';
import { useStore } from 'src/onboarding/store/store';
import axios from 'axios';
import { debounce } from 'lodash';
import { getEnvUrl } from 'src/onboarding/helpers/getEnvUrl';
import { useNav } from 'src/onboarding/hooks/nav';
import { Allergies, UpdateUserRequest } from 'src/onboarding/types/api';
import { getEarlyAchievableOffset } from 'src/onboarding/helpers/getEarlyAchievableOffset';
import { getAdjustedDate } from 'src/onboarding/helpers/getAdjustedDate';
import { trackGoogleAdsTrialConversion } from 'src/onboarding/helpers/googleAdsConversion';

const usersPercentToActivateScrollMetric =
  process.env.REACT_APP_USERS_PERCENT_TO_ACTIVATE_SCROLL_METRIC;
const tKeyPlans = 'core.plans';

export interface ProductsTexts {
  titleText: string;
  subTitleText: string;
  perText: string;
  perTitle: string;
  id: string;
}

export interface SummaryProps {
  landingStack?: string[];
  isApiReady?: boolean;
  isLoadingApp?: boolean;
  sections?: { [key: string]: React.FC };
  landingButtons?: InterButtonConfig[];
  buttonsConfigs?: { [key in ButtonPlacementType]: ButtonConfig };
  header?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  plansSubtitle?: React.ReactNode;
  paymentModalTitle?: PaymentModalTitle;
  paymentModalSubtitle?: React.ReactNode;
  soInfoImage?: ImageSource;
  // onSuccess?: (purchase?: any) => void,
  onNextClick?: () => void;
  isPurchaseProgress?: boolean;
  showDownSaleModalOnce: boolean;
  setShowDownSaleModalOnce: (param: boolean) => void;
}

interface PlansRef {
  onPayment: () => void;
}

const PersonalPlans = forwardRef<PlansRef, SummaryProps>((props, ref) => {
  const {
    header,
    footer,
    className,
    plansSubtitle,
    paymentModalTitle,
    paymentModalSubtitle,
    soInfoImage,
    onNextClick,
    isPurchaseProgress = false,
    showDownSaleModalOnce,
    setShowDownSaleModalOnce,
  } = props;

  const dispatch = useDispatch();
  const apiAuthKey = useAppSelector((state) => {
    const tokenType = state.profile.apiAuth?.token_type;
    const accessToken = state.profile.apiAuth?.access_token;
    return tokenType && accessToken ? `${tokenType} ${accessToken}` : '';
  });
  const isApiReady = Boolean(apiAuthKey);
  const isLoadingApp = useAppSelector((state) => !state.app.loaded);
  const ppRef = useRef<PaymentProcessorRef>(null);
  const setPage = useStore((state) => state.setPage);
  const userId = useStore((state) => state.userId);
  const trialPrice = useStore((state) => state.trialPrice);
  const startTime = useRef(performance.now());
  const [lcp, setLcp] = useState<number | null>(null);

  const state = useStore((state) => state);
  const {
    email,
    born: age,
    gender,
    preferences: dietary,
    height,
    weight,
    idealWeight,
    when,
    exclude,
    goForWalks,
    name: userName,
    breakfast,
    snacks,
  } = state;

  const { onNext } = useNav();

  useEffect(() => {
    const observer = new PerformanceObserver((list) => {
      const entries: any = list.getEntries();
      setLcp(entries?.[0].renderTime);
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });

    return () => {
      observer.disconnect();
    };
  }, []);

  useImperativeHandle(ref, () => ({
    onPayment() {
      handleButtonClick(ButtonPlacementType.PLANS);
    },
  }));

  const isLoadingBilling = useSelector(selectLoading);
  const products = useSelector(selectProducts);
  const specialOfferEndDate = useSelector(selectSpecialOfferEndDate);
  const noFundsOfferEndDate = useSelector(selectNoFundsOfferEndDate);

  useEffect(() => {
    if (products.length > 0 && lcp) {
      const endTime = performance.now();
      const productsLoading = `${((endTime - startTime.current) / 1000).toFixed(2)} sec`;
      Analytics.trackEvent(`lcp`, 'metric', {
        lcp: `${(lcp / 1000).toFixed(2)} sec`,
        productsLoading,
      });
    }
  }, [products.length, lcp]);

  //@ts-ignore
  const orderDetails = useSelector((state) => state.billing.orderDetails);
  //@ts-ignore
  const isLoaded = useSelector((state) => !state.billing.loading);
  const [selectedSpecialOfferProduct, setSelectedSpecialOfferProduct] = useState(null);
  const specialOfferProducts = useSelector(selectSpecialOfferProducts);
  //@ts-ignore
  const { title, highlightWords } = useSelector((state) => state.remoteConfig.specialOffer);
  useEffect(() => {
    if (specialOfferProducts) {
      //@ts-ignore
      setSelectedSpecialOfferProduct(specialOfferProducts[0]);
    }
  }, [specialOfferProducts, isLoaded]);

  const {
    openPaymentModalOnProductClick,
    inertialApplePay,
    quizPaymentModalFullMode,
    isPaymentModalShowAfterQuiz,
    isInjectedPaymentMethodOnPage,
    specialOffer,
    noFundsOffer,
    //@ts-ignore
    landingStack,
    buttonsConfigs,
    showSpecialOffer,
    routeGroups: flow,
  } = useRemoteConfig();

  const selectedProduct = products.find((product) => product.subTitle.en === trialPrice);

  const shouldUseSelectedProduct =
    !!trialPrice && flow.flat().includes('trial-price') && selectedProduct;

  const [activeProduct, setActiveProduct] = useState<any>(
    shouldUseSelectedProduct
      ? selectedProduct
      : products.find((p) => p.default) || products[0] || {}
  );
  const [countPPClosed, setCountPPClose] = useState(0);
  const [soOpen, setSOOpen] = useState(false);
  const [nfoOpen, setNFOOpen] = useState(false);

  const { apiBaseUrl } = getEnvUrl();

  const isShowInertialApplePay = useShowInertialApplePay();
  const isSummaryReady = !isLoadingApp && !isLoadingBilling;

  useSummaryViewedRate(usersPercentToActivateScrollMetric, isSummaryReady);

  const debouncedTrackEvent = useCallback(
    debounce((products) => {
      Analytics.trackEvent(`products`, 'empty', { products });
    }, 2000),
    []
  );

  useEffect(() => {
    if (products?.length === 0) {
      debouncedTrackEvent(products);
    }

    return () => {
      debouncedTrackEvent.cancel();
    };
  }, [products, debouncedTrackEvent]);

  useEffect(() => {
    if (!showDownSaleModalOnce) {
      //@ts-ignore
      setActiveProduct(
        shouldUseSelectedProduct
          ? selectedProduct
          : products.find((p) => Object.values(p).includes(activeProduct?.id)) ||
              products.find((p) => p.default) ||
              products[0] ||
              activeProduct
      );
    } else {
      //@ts-ignore
      setActiveProduct(
        specialOfferProducts.find((p) => Object.values(p).includes(activeProduct?.id)) ||
          specialOfferProducts.find((p) => p.default) ||
          specialOfferProducts[0] ||
          activeProduct
      );
    }
  }, [
    activeProduct,
    products,
    setActiveProduct,
    showDownSaleModalOnce,
    specialOfferProducts,
    shouldUseSelectedProduct,
  ]);

  useEffect(() => {
    if (isApiReady) {
      dispatch(init());
    }
  }, [dispatch, isApiReady, apiAuthKey]);

  const handleProductButtonClick = useCallback(() => {
    if (isShowInertialApplePay && !isInjectedPaymentMethodOnPage) {
      const b = (document.querySelector('apple-pay-button') ||
        document.querySelector('#apple-pay') ||
        document.querySelector('button[label="apple"]')) as HTMLButtonElement | null;
      if (b) {
        try {
          Analytics.trackEvent('apple_pay', 'inertial');
          b.click();
          if (inertialApplePay !== InertialApplePay.ENABLED_WITH_DEFAULT) {
            return;
          }
        } catch (ex) {}
      }
    }

    setSOOpen(false);
    ppRef.current?.showPaymentPopup();
  }, [inertialApplePay, isInjectedPaymentMethodOnPage, isShowInertialApplePay]);

  const handleButtonClick = useCallback(
    (type?: ButtonPlacementType, forceOpen?: boolean) => {
      const behavior = type ? buttonsConfigs[type]?.behavior : undefined;

      if (type === ButtonPlacementType.PLANS) {
        handleProductButtonClick();
      } else if (!landingStack.includes(CoreSummarySection.PLANS) && onNextClick) {
        onNextClick();
      } else if (behavior === ButtonBehavior.NEXT && onNextClick) {
        onNextClick();
      } else if (behavior === ButtonBehavior.SCROLL && !forceOpen) {
        scrollToSection('#plans');
      } else {
        handleProductButtonClick();
      }
    },
    [buttonsConfigs, handleProductButtonClick, onNextClick, landingStack]
  );

  const handleInterButtonClick = useCallback(
    (config: InterButtonConfig) => {
      const behavior = config.behavior;

      if (
        (!landingStack.includes(CoreSummarySection.PLANS) || behavior === ButtonBehavior.NEXT) &&
        onNextClick
      ) {
        onNextClick();
      } else if (
        behavior === ButtonBehavior.SCROLL &&
        landingStack.includes(CoreSummarySection.PLANS)
      ) {
        scrollToSection('#plans');
      } else {
        handleProductButtonClick();
      }
    },
    [handleProductButtonClick, onNextClick, landingStack]
  );

  const handleProductClick = useCallback(
    (product: ProductDetails) => {
      setActiveProduct(product);
      if (showDownSaleModalOnce) {
        setActiveProduct(product);
      } else {
        setActiveProduct(product);
      }

      if (openPaymentModalOnProductClick) {
        Analytics.trackEvent('product', 'click', { selectedProductId: product.id });
        handleButtonClick(ButtonPlacementType.PLANS);
      }
    },
    [activeProduct, openPaymentModalOnProductClick, handleButtonClick, setActiveProduct]
  );

  const handlePPClose = useCallback(() => {
    setCountPPClose((p) => p + 1);
    if (!showDownSaleModalOnce) {
      handleShowDownSaleModal();
    }
  }, [specialOfferProducts, selectedSpecialOfferProduct, showDownSaleModalOnce]);

  const handleOfferTriggered = useCallback(
    (type: 'specialOffer' | 'noFundsOffer') => {
      specialOffer.scrollToTopAfterTrigger && window.scrollTo({ top: 0, behavior: 'smooth' });

      switch (type) {
        case 'noFundsOffer': {
          dispatch(initNoFundsOfferEndDate());
          setNFOOpen(true);

          break;
        }
        case 'specialOffer': {
          dispatch(initSpecialOfferEndDate());
          setSOOpen(true);

          break;
        }
      }
    },
    [specialOffer.scrollToTopAfterTrigger, dispatch]
  );

  useEffect(() => {
    const isSOTriggered =
      !specialOfferEndDate &&
      specialOffer.enabled &&
      specialOffer.showAfterCloseAmount === countPPClosed;

    if (isSOTriggered) {
      handleOfferTriggered('specialOffer');
    }
  }, [
    countPPClosed,
    specialOffer.enabled,
    specialOffer.showAfterCloseAmount,
    specialOfferEndDate,
    handleOfferTriggered,
  ]);

  const handleSOClose = useCallback(() => {
    setSOOpen(false);
  }, []);

  const handleNFOClose = useCallback(() => {
    setNFOOpen(false);
  }, []);

  const handlePaymentError = useCallback(
    (error: any) => {
      const isSONoFundsTriggered =
        error?.code === SOLIDGATE_ERROR_CODES.INSUFFICIENT_FUNDS &&
        noFundsOffer.enabled &&
        !noFundsOfferEndDate &&
        error?.entity === 'form';

      if (!activeProduct?.isOneTimePurchase && isSONoFundsTriggered) {
        handleOfferTriggered('noFundsOffer');
      }
    },
    [
      activeProduct?.isOneTimePurchase,
      noFundsOffer.enabled,
      noFundsOfferEndDate,
      handleOfferTriggered,
    ]
  );

  const handleInjectedSubmit = useCallback((data: any) => {
    ppRef.current?.processFormData(data);
  }, []);

  const handleSubscribeOnOneClick = useCallback((productId: string) => {
    ppRef.current?.subscribeOnOneClick({ productId });

    setNFOOpen(false);
  }, []);

  const isShowPopUpComponent = isLoaded && selectedSpecialOfferProduct;
  const downSaleModalRef = useRef(null);

  const handleShowDownSaleModal = useCallback(() => {
    if (showSpecialOffer) {
      if (specialOffer.enabled && !showDownSaleModalOnce) {
        setTimeout(() => {
          if (selectedSpecialOfferProduct) {
            setActiveProduct(selectedSpecialOfferProduct);
          }
          Analytics.trackEvent('special_offer_modal', 'open');

          downSaleModalRef.current.showPaymentPopup();
          // document.body.style.overflow = 'hidden';
          setShowDownSaleModalOnce(true);
        }, 500);
      }
    }
  }, [showDownSaleModalOnce, selectedSpecialOfferProduct, showSpecialOffer]);

  const ACTIVE_SHOW_PRODUCTS = showDownSaleModalOnce
    ? specialOfferProducts
    : shouldUseSelectedProduct
      ? [selectedProduct]
      : products;

  const renderSections = () => (
    <Plans
      subtitle={plansSubtitle}
      activeProduct={activeProduct}
      // products={ACTIVE_SHOW_PRODUCTS}
      // onProductClick={handleProductClick}
      onFixedButtonClick={handleButtonClick}
      onPayPalSubmit={handleInjectedSubmit}
      productsTexts={getProductsTexts(products, tKeyPlans)}
    />
  );

  const renderSpecialOffer = () => {
    switch (specialOffer.mode) {
      case 'with_plans':
      case 'with_plans_v2':
        return (
          <SpecialOfferWithPlans
            isOpen={soOpen}
            activeProduct={activeProduct}
            onProductClick={handleProductClick}
            onGetClick={handleProductButtonClick}
            onClose={handleSOClose}
            productsTexts={getProductsTexts(products, specialOfferTKey)}
          />
        );
      case 'info':
        return (
          <SpecialOfferInfo
            isOpen={soOpen}
            offerText={specialOffer?.soInfoOfferText}
            image={soInfoImage}
            onGetClick={handleSOClose}
            onClose={handleSOClose}
          />
        );
      default:
        return null;
    }
  };

  const adjustedDate = getAdjustedDate(when, getEarlyAchievableOffset(when));

  const showNewTargetDate =
    getEarlyAchievableOffset(when) !== null && flow.flat().includes('goal-2');

  const onSuccess = async (purchase?: Subscription) => {
    try {
      trackGoogleAdsTrialConversion(purchase, activeProduct);
      const userData: UpdateUserRequest = {
        email,
        subscription: {
          isActive: true,
        },
        ...(age && { age }),
        gender,
        activity_level: goForWalks,
        dietary,
        allergies: Object.fromEntries(
          ['no_allergies', 'gluten', 'wheat', 'lactose', 'nuts', 'eggs', 'fish', 'meat'].map(
            (allergy) => [allergy, exclude.includes(allergy)]
          )
        ) as Allergies,
        height:
          height.type === 'ft'
            ? {
                ft: height.ft,
                type: 'ft',
              }
            : {
                cm: Number(height.cm),
                type: 'cm',
              },
        weight: {
          kg: weight.type === 'kg' ? Number(weight.kg) : undefined,
          lbs: weight.type === 'lbs' ? Number(weight.lbs) : undefined,
          type: weight.type,
        },
        targetWeight: {
          kg: idealWeight.type === 'kg' ? Number(idealWeight.value) : undefined,
          lbs: idealWeight.type === 'lbs' ? Number(idealWeight.value) : undefined,
          type: idealWeight.type,
        },
        targetWeightDate: showNewTargetDate
          ? new Date(adjustedDate.year, +adjustedDate.month)
          : new Date(+when.year, +when.month - 1, +when.day),
        name: userName,
        preferredDailyMeals: {
          breakfast: breakfast,
          snack: snacks,
          lunch: true,
          dinner: true,
        },
      };
      await axios.put(`${apiBaseUrl}/user/update-by-token/${userId}`, userData);
      Analytics.trackEvent('user-update-token', 'success');
    } catch (error) {
      Analytics.trackEvent('user-update-token', 'error');
      console.error('Error:', error);
    }
    onNext();
  };

  return (
    <div className={classNames(classes.wrap, className)}>
      {/* {!isSummaryReady ? (
        <Preloader />
      ) : (
        <> */}
      {header}

      {renderSections()}

      {!landingStack.includes(CoreSummarySection.PLANS) && (
        <FixedButton
          activeProduct={activeProduct}
          buttonsConfigs={buttonsConfigs}
          onlyFixed
          onClick={handleButtonClick}
          classStyle='style'
        />
      )}

      {footer}
      {/* </>
      )} */}
      {!!landingStack.includes(CoreSummarySection.PLANS) && (
        <PaymentProcessor
          ref={ppRef}
          paymentModalTitle={paymentModalTitle}
          paymentModalSubtitle={paymentModalSubtitle}
          activeProduct={activeProduct}
          orderDetails={orderDetails}
          fullMode={quizPaymentModalFullMode}
          shownByDefault={isPaymentModalShowAfterQuiz}
          onSuccess={onSuccess}
          onError={handlePaymentError}
          onClose={handlePPClose}
          isPurchaseProgress={isPurchaseProgress}
        />
      )}
      {specialOffer.enabled && renderSpecialOffer()}
      {noFundsOffer.enabled && (
        <SpecialOfferNoFunds
          isOpen={nfoOpen}
          activeProductIndex={products.findIndex(({ id }) => id === activeProduct.id) || 0}
          discount={noFundsOffer.discount}
          buttonTitle={noFundsOffer?.buttonTitle}
          currentPriceText={noFundsOffer?.currentPriceText}
          onGetClick={handleSubscribeOnOneClick}
          onClose={handleNFOClose}
        />
      )}
      <TrialPrice ready={isSummaryReady} />
      {isShowPopUpComponent && (
        <DownSaleOfferModal
          //@ts-ignore
          activeProduct={activeProduct}
          products={specialOfferProducts}
          showTerms={true}
          showButton={true}
          onProductClick={handleProductClick}
          onButtonClick={handleButtonClick}
          onPayPalSubmit={handleButtonClick}
          title={title}
          highlightWords={highlightWords}
          ref={downSaleModalRef}
        />
      )}
    </div>
  );
});

export default React.memo(PersonalPlans);
